'use strict';

import {
    ACESFilmicToneMapping,
    BufferGeometry,
    CameraHelper,
    CatmullRomCurve3,
    CineonToneMapping,
    Euler,
    Group,
    Line,
    LinearToneMapping,
    LineBasicMaterial,
    MathUtils,
    NoToneMapping,
    ReinhardToneMapping,
    TextureLoader,
    Vector3,
    Color,
    Box3,
    SpotLight,
    Object3D, LinearSRGBColorSpace, SRGBColorSpace, SphereGeometry,
} from 'three';
import {
    Buildings,
    isDesktop,
    isMobile, projectMode,
    resourcesFolder,
    threeJsVariables,
} from '../../../settings';
import { Sky } from 'three/examples/jsm/objects/Sky';
import * as dat from 'dat.gui';
import { TimesOfDay } from '../litemode/dayNight/TimesOfDay';
import gsap from "gsap";
import { updateShadowMap } from '../../threejs/lights';
import { OSMEnv } from '../../threejs/sceneBackground';
import { Lerp } from '../../projectTime/Lerp';
import { setTilesEnvMapIntensity } from '../../threejs/environment';
import { sceneObjectsGroup } from '../../points/points';
import skyFragment from './sky/SkyFragment.glsl';
import skyVertex from './sky/SkyVertex.glsl';
import { clamp } from '../../../functions';
import { Switch } from '../../interfaces/switchers/Switch';
import Stats from 'three/examples/jsm/libs/stats.module';
import {changeLangToCurrent} from "../../components/langSwitcher/langSwitcher";
import { DisplayOptions } from '../../components/displayOptions/DisplayOptions';
import { Environment } from '../../components/Environment/Environment';
import { BmbyMode } from '../../mode/BmbyMode';
import { DayNightButton } from '../../threejs/DayNightButton/DayNightButton';
import {BaseElement} from "../../interfaces/BaseElement";
import { PublicLayers } from '../../components/publicLayers/PublicLayers';
import { OrbitSky } from './sky/OrbitSky';

export class OrbitDayAndNight extends TimesOfDay {
    static active = config.features.dayNight?.active || false;
    static osmSelected = config.features.dayNight?.checked || false;
    static scope = null;
    static usePresets = !Environment.fromBundle();
    static buildingMaterials = [];

    #debug = config.features.dayNight?.debug || false;
    #settings = config.features.dayNight?.settings || {};


    constructor (parent,response) {
        super(parent,response);

        if ((!OrbitDayAndNight.active && !TimesOfDay.active) || (OrbitDayAndNight.active && projectMode.AirMode.active && !projectMode.AirMode.hasDayAndNight())) {
            this.removeFromDisplayOptions();
            return;
        }

        if (isMobile) {

            this.currentTimeMainContainer.classList.add('orbit-time','orbit-time-hidden');

        } else {
            this.currentTimeMainContainer.classList.add('orbit-time');
        }

        this.osm = new OSMEnv();

        //Shadows
        // const shadowsContainer = createHtmlElement('','','div',['current-time__shadow-container']);
        const shadowsContainer = new BaseElement()
            .addClass('current-time__shadow-container');
        // const shadowTitle = createHtmlElement(shadowsContainer.DOMElement, changeLangToCurrent('Enable shadows'),'span',['current-time__shadow-title','language-string']);
        // shadowTitle.setAttribute('data-dictionary','Enable shadows');
        new BaseElement('span')
            .addClass('current-time__shadow-title','language-string')
            .innerHtml(changeLangToCurrent('Enable shadows'))
            .addAttribute('data-dictionary','Enable shadows')
            .appendTo(shadowsContainer.DOMElement);
        this.shadowSwitcher = new Switch(shadowsContainer.DOMElement);
        this.shadowSwitcher.listener = this.shadowsHandler.bind(this);
        this.shadowSwitcher.checked = this.#settings.shadows || Boolean(isDesktop);
        const currentTimeToggleContainer = this.currentTimeMainContainer.querySelector('.current-time__toggle');

        currentTimeToggleContainer.style.marginBottom = '10px';
        currentTimeToggleContainer.insertAdjacentElement('afterend',shadowsContainer.DOMElement);


        this.center = {
            lat: Number(config.localization.latitude),
            lng: Number(config.localization.longitude)
        };
        this.points = [];
        this.prevValue = 0;
        this.alphaDirection = 0;
        this.reset = false;
        this.directionalLight = threeJsVariables.scene.getObjectByName('Dir. Light');
        this.ambientLight = threeJsVariables.scene.getObjectByName('ambientLight');
        this.secondDirectionalLight = threeJsVariables.scene.getObjectByName('Second directional light');

        if (OrbitDayAndNight.usePresets) {
            const buildingIntensity = Buildings.getBuildingsArray().length ?
                Array.isArray(Buildings.getBuildingsArray()[0].instances[0].material) ?
                    Buildings.getBuildingsArray()[0].instances[0].material[0].envMapIntensity :
                    Buildings.getBuildingsArray()[0].instances[0].material.envMapIntensity : 1.0;

            this.defaultLightSettings = {
                directionalLightPosition: new Vector3().copy(this.directionalLight.position),
                directionalLightIntensity: this.directionalLight.intensity,
                directionalLightColor: new Color().copy(this.directionalLight.color),
                ambientIntensity: this.ambientLight.intensity,
                ambientColor: new Color().copy(this.ambientLight.color),
                buildingIntensity: buildingIntensity,
                sceneObjects: {}
            };

            sceneObjectsGroup.traverse(el => {
                if (el.isMesh) {
                    if (Array.isArray(el.material)) {
                        this.defaultLightSettings.sceneObjects[el.name] = el.material.map(subMaterial => subMaterial.envMapIntensity);
                    } else {
                        this.defaultLightSettings.sceneObjects[el.name] = el.material.envMapIntensity;
                    }
                }
            });
        }

        this.lightSaveSettings = {
            directional: {
                position: new Vector3().copy(this.directionalLight.position),
                intensity: this.directionalLight.intensity,
                color: new Color().copy(this.directionalLight.color)
            },
            ambient: {
                intensity: this.ambientLight.intensity,
                color: new Color().copy(this.ambientLight.color)
            }
        };

        this.activePreset = null;
        this.currentPreset = {
            turbidity: 0,
            rayleigh: 0,
            mieCoefficient: 0,
            mieDirectionalG: 0,
            directionalIntensity: 0,
            directionalColor: new Color().copy(this.lightSaveSettings.directional.color),
            ambientIntensity: 0,
            ambientColor: new Color().copy(this.lightSaveSettings.ambient.color),
            cloudsColor: new Color().setStyle('#ffffff',LinearSRGBColorSpace),
            seaColor: new Color().setStyle('#386F90',LinearSRGBColorSpace),
            seaWaveColor: new Color().setStyle('#6a9ebd',LinearSRGBColorSpace),
            treeColor: new Color().setStyle('#ffffff',LinearSRGBColorSpace),
            osmLightMapIntensity: 0,
            osmEnvMapIntensity: 1,
            tilesIntensity: 1,
            buildingIntensity: 1,
            pointLightIntensity: 0,
            buildingEmissiveIntensity: 0,
            stars: 0,
            sun: true,
            timeLine: 0
        };

        this.range.addListener('input',(e) => this.rangeHandler(e,true));
        // this.range.addListener('change',() => this.rangeOutput.addClass('hidden'));
        // this.range.addListener('pointerdown',() => this.rangeOutput.removeClass('hidden'));
        this.switcher.listener = this.switcherHandler.bind(this);
        this.switcher.checked = config.features.dayNight.currentTime;

        this.buildSunPath();
        this.#addDebug();
        OrbitDayAndNight.usePresets && this.addPointLight();
        
        this.dayInfo = this.getDayTimeLine();
        
        this.currentTime = this.dayInfo.currentTime;
        this.sunTimePath = (this.dayInfo.sunset - this.dayInfo.sunrise);
        this.currentRangeValue = Number(this.range.element.value);
        this.minRangeValue = this.dayInfo.dayStart;
        this.maxRangeValue = this.dayInfo.dayEnd;

        OrbitDayAndNight.sky && OrbitDayAndNight.sky.material.uniforms['sunPosition'].value.copy(this.points[0]);
        OrbitDayAndNight.sky && OrbitDayAndNight.sky.shaderVersion === '2.0' && OrbitDayAndNight.sky.updateUniforms();

        if (this.#settings.timeLimits) {
            const minLimit = this.#settings.timeLimits.min;
            const maxLimit = this.#settings.timeLimits.max;
            if (minLimit) {
                const minTime = minLimit.split(':');
                this.minRangeValue = new Date(this.currentTime).setHours(Number(minTime[0]),Number(minTime[1]));
            }
            if (maxLimit) {
                const maxTime = maxLimit.split(':');
                this.maxRangeValue = new Date(this.currentTime).setHours(Number(maxTime[0]),Number(maxTime[1]));
            }
        }

        if (OrbitDayAndNight.usePresets) {
            this.buildPresets();
        } else {
            Environment.setDayTime(this.dayInfo);
            Environment.setTime(this.currentRangeValue);
        }

        // console.log(this.switcher.checkbox.checked)

        // if (!this.switcher.checkbox.checked || (isMobile)) {
            const time = this.#settings.time.split(':');
            const hours = Number(time[0]);
            const minutes = Number(time[1]);
            this.manualTime = new Date(this.currentTime).setHours(hours,minutes);
            this.currentTime = this.manualTime;
            this.updateTimesOfDay(this.currentTime,true);
            this.range.element.value = this.currentTime;
            DayNightButton.scope?.setSwitcherState(this.currentTime, this.dayInfo);
        // }

        const event = new Event('change');
        this.switcher.checkbox.dispatchEvent(event);
        this.shadowSwitcher.checkbox.dispatchEvent(event);
        DisplayOptions.displayOptionsVisibility('update');
        OrbitDayAndNight.scope = this;
    }

    shadowsHandler(e) {

        switch (e.target.checked) {

            case true:
                this.directionalLight.castShadow = true;
                break;
            case false:
                this.directionalLight.castShadow = false;
                break;

        }

        updateShadowMap();
    }

    presetsCorrection(presetName, presetProperty,defaultValue) {
        if (this.#settings.presetsCorrection &&
            this.#settings.presetsCorrection[presetName] &&
            this.#settings.presetsCorrection[presetName][presetProperty]) {

            return this.#settings.presetsCorrection[presetName][presetProperty];
        } else {
            return defaultValue;
        }
    }

    buildPresets() {

        this.nightPreset = {
            turbidity: new Lerp(0.1, 0.1),
            rayleigh: new Lerp(0.1, 0.1),
            mieCoefficient: new Lerp(0.0005,0.0005),
            mieDirectionalG: new Lerp(0.025,0.025),
            directionalIntensity: new Lerp(
                0 + this.presetsCorrection('nightPreset','directionalIntensity',0),
                0 + this.presetsCorrection('nightPreset','directionalIntensity',0)
            ),
            directionalColor: new Color().copy(this.lightSaveSettings.directional.color),
            ambientIntensity: new Lerp(
                1 + this.presetsCorrection('nightPreset','ambientIntensity',0),
                1 + this.presetsCorrection('nightPreset','ambientIntensity',0)
            ),
            ambientColor: new Color().setStyle(this.presetsCorrection('nightPreset','ambientColor','#000721'),LinearSRGBColorSpace),
            cloudsColor: new Color().setStyle(this.presetsCorrection('nightPreset','cloudsColor','#252934'),LinearSRGBColorSpace),
            seaColor: new Color().setStyle(this.presetsCorrection('nightPreset','seaColor','#161835'),LinearSRGBColorSpace),
            seaWaveColor: new Color().setStyle(this.presetsCorrection('nightPreset','seaWaveColor','#6f6f6f'),LinearSRGBColorSpace),
            treeColor: new Color().setStyle(this.presetsCorrection('nightPreset','treeColor','#474747'),LinearSRGBColorSpace),
            osmLightMapIntensity: new Lerp(
                1 + this.presetsCorrection('nightPreset','osmLightMapIntensity',0),
                1 + this.presetsCorrection('nightPreset','osmLightMapIntensity',0)
            ),
            osmEnvMapIntensity: new Lerp(
                0.3 + this.presetsCorrection('nightPreset','osmEnvMapIntensity',0),
                0.3 + this.presetsCorrection('nightPreset','osmEnvMapIntensity',0)
            ),
            tilesIntensity: new Lerp(
                0.3 + this.presetsCorrection('nightPreset','tilesIntensity',0),
                0.3 + this.presetsCorrection('nightPreset','tilesIntensity',0)
            ),
            buildingIntensity: new Lerp(
                0.3 + this.presetsCorrection('nightPreset','buildingIntensity',0),
                0.3 + this.presetsCorrection('nightPreset','buildingIntensity',0)
            ),
            pointLightIntensity: new Lerp(
                1.3 + this.presetsCorrection('nightPreset','pointLightIntensity',0),
                1 + this.presetsCorrection('nightPreset','pointLightIntensity',0)
            ),
            buildingEmissiveIntensity: new Lerp(
                1.5 + this.presetsCorrection('nightPreset','buildingEmissiveIntensity',0),
                1 + this.presetsCorrection('nightPreset','buildingEmissiveIntensity',0)
            ),
            stars: new Lerp(
                1 + this.presetsCorrection('nightPreset','stars',0),
                1 + this.presetsCorrection('nightPreset','stars',0)
            ),
            sun: false,
            timeLine: this.dayInfo.dayStart - this.dayInfo.beforeSunrise,
        };

        this.beforeSunrisePreset = {
            turbidity: new Lerp(this.nightPreset.turbidity.to,0.4),
            rayleigh: new Lerp(this.nightPreset.rayleigh.to,1.34),
            mieCoefficient: new Lerp(this.nightPreset.mieCoefficient.to,0),
            mieDirectionalG: new Lerp(this.nightPreset.mieDirectionalG.to,0.013),
            directionalIntensity: new Lerp(
                this.nightPreset.directionalIntensity.to,
                0 + this.presetsCorrection('beforeSunrisePreset','directionalIntensity',0)
            ),
            directionalColor: new Color().copy(this.lightSaveSettings.directional.color),
            ambientIntensity: new Lerp(
                this.nightPreset.ambientIntensity.to,
                0 + this.presetsCorrection('beforeSunrisePreset','ambientIntensity',0)
            ),
            ambientColor: new Color().setStyle(this.presetsCorrection('beforeSunrisePreset','ambientColor','#000721'),LinearSRGBColorSpace),
            cloudsColor: new Color().setStyle(this.presetsCorrection('beforeSunrisePreset','cloudsColor','#252934'),LinearSRGBColorSpace),
            seaColor: new Color().setStyle(this.presetsCorrection('beforeSunrisePreset','seaColor','#161835'),LinearSRGBColorSpace),
            seaWaveColor: new Color().setStyle(this.presetsCorrection('beforeSunrisePreset','seaWaveColor','#6f6f6f'),LinearSRGBColorSpace),
            treeColor: new Color().setStyle(this.presetsCorrection('beforeSunrisePreset','treeColor','#474747'),LinearSRGBColorSpace),
            osmLightMapIntensity: new Lerp(
                this.nightPreset.osmLightMapIntensity.to,
                0.5 + this.presetsCorrection('beforeSunrisePreset','osmLightMapIntensity',0)
            ),
            osmEnvMapIntensity: new Lerp(
                this.nightPreset.osmEnvMapIntensity.to,
                0.4 + this.presetsCorrection('beforeSunrisePreset','osmEnvMapIntensity',0)
            ),
            tilesIntensity: new Lerp(
                this.nightPreset.tilesIntensity.to,
                0.4 + this.presetsCorrection('beforeSunrisePreset','tilesIntensity',0)
            ),
            buildingIntensity: new Lerp(
                this.nightPreset.buildingIntensity.to,
                0.4 + this.presetsCorrection('beforeSunrisePreset','buildingIntensity',0)
            ),
            pointLightIntensity: new Lerp(
                this.nightPreset.pointLightIntensity.to,
                0 + this.presetsCorrection('beforeSunrisePreset','pointLightIntensity',0)
            ),
            buildingEmissiveIntensity: new Lerp(
                this.nightPreset.buildingEmissiveIntensity.to,
                0.5 + this.presetsCorrection('beforeSunrisePreset','buildingEmissiveIntensity',0)
            ),
            stars: new Lerp(
                this.nightPreset.stars.to,
                0.5 + this.presetsCorrection('beforeSunrisePreset','stars',0)
            ),
            sun: false,
            timeLine: this.dayInfo.beforeSunrise - this.dayInfo.sunrise
        };

        this.sunrisePreset = {
            turbidity: new Lerp(1.7,0.2),
            rayleigh: new Lerp(1.08,0.268),
            mieCoefficient: new Lerp(0.002,0.001),
            mieDirectionalG: new Lerp(0.999,0.999),
            directionalIntensity: new Lerp(
                this.beforeSunrisePreset.directionalIntensity.to,
                0.7 + this.presetsCorrection('sunrisePreset','directionalIntensity',0)
            ),
            directionalColor: new Color().copy(this.lightSaveSettings.directional.color),
            ambientIntensity: new Lerp(
                this.beforeSunrisePreset.ambientIntensity.to,
                0 + this.presetsCorrection('sunrisePreset','ambientIntensity',0)
            ),
            ambientColor: new Color().copy(this.lightSaveSettings.ambient.color),
            cloudsColor: new Color().setStyle(this.presetsCorrection('sunrisePreset','cloudsColor','#c5cbd6'),LinearSRGBColorSpace),
            seaColor: new Color().setStyle(this.presetsCorrection('sunrisePreset','seaColor','#284f66'),LinearSRGBColorSpace),
            seaWaveColor: new Color().setStyle(this.presetsCorrection('sunrisePreset','seaWaveColor','#9b9b9b'),LinearSRGBColorSpace),
            treeColor: new Color().setStyle(this.presetsCorrection('sunrisePreset','treeColor','#c7c7c7'),LinearSRGBColorSpace),
            osmLightMapIntensity: new Lerp(
                this.beforeSunrisePreset.osmLightMapIntensity.to,
                0 + this.presetsCorrection('sunrisePreset','osmLightMapIntensity',0)
            ),
            osmEnvMapIntensity: new Lerp(
                this.beforeSunrisePreset.osmEnvMapIntensity.to,
                1.0 + this.presetsCorrection('sunrisePreset','osmEnvMapIntensity',0)
            ),
            tilesIntensity: new Lerp(
                this.beforeSunrisePreset.tilesIntensity.to,
                1 + this.presetsCorrection('sunrisePreset','tilesIntensity',0)
            ),
            buildingIntensity: new Lerp(
                this.beforeSunrisePreset.buildingIntensity.to,
                1 + this.presetsCorrection('sunrisePreset','buildingIntensity',0)
            ),
            pointLightIntensity: new Lerp(0,0),
            buildingEmissiveIntensity: new Lerp(
                this.beforeSunrisePreset.buildingEmissiveIntensity.to,
                0 + this.presetsCorrection('sunrisePreset','buildingEmissiveIntensity',0)
            ),
            stars: new Lerp(
                this.beforeSunrisePreset.stars.to,
                0 + this.presetsCorrection('sunrisePreset','stars',0)
            ),
            sun: true,
            timeLine: this.dayInfo.sunrise - this.dayInfo.afterSunrise
        };

        this.zenitPreset = {
            turbidity: new Lerp(this.sunrisePreset.turbidity.to,0.2),
            rayleigh: new Lerp(this.sunrisePreset.rayleigh.to,0.5),
            mieCoefficient: new Lerp(this.sunrisePreset.mieCoefficient.to,0.01),
            mieDirectionalG: new Lerp(this.sunrisePreset.mieDirectionalG.to,1),
            directionalIntensity: new Lerp(
                this.sunrisePreset.directionalIntensity.to,
                0.96 + this.presetsCorrection('zenitPreset','directionalIntensity',0)
            ),
            directionalColor: new Color().setStyle(this.presetsCorrection('zenitPreset','directionalColor','#ffe6c3'),LinearSRGBColorSpace),
            ambientIntensity: new Lerp(
                this.sunrisePreset.ambientIntensity.to,
                0.16 + this.presetsCorrection('zenitPreset','ambientIntensity',0)
            ),
            ambientColor: new Color().setStyle(this.presetsCorrection('zenitPreset','ambientColor','#c2dbff'),LinearSRGBColorSpace),
            cloudsColor: new Color().setStyle(this.presetsCorrection('zenitPreset','cloudsColor','#ffffff'),LinearSRGBColorSpace),
            seaColor: new Color().setStyle(this.presetsCorrection('zenitPreset','seaColor','#51bacb'),LinearSRGBColorSpace),
            seaWaveColor: new Color().setStyle(this.presetsCorrection('zenitPreset','seaWaveColor','#ffffff'),LinearSRGBColorSpace),
            treeColor: new Color().setStyle(this.presetsCorrection('zenitPreset','treeColor','#ffffff'),LinearSRGBColorSpace),
            osmLightMapIntensity: new Lerp(
                this.sunrisePreset.osmLightMapIntensity.to,
                0 + this.presetsCorrection('zenitPreset','osmLightMapIntensity',0)
            ),
            osmEnvMapIntensity: new Lerp(
                this.sunrisePreset.osmEnvMapIntensity.to,
                1.0 + this.presetsCorrection('zenitPreset','osmEnvMapIntensity',0)
            ),
            tilesIntensity: new Lerp(
                this.sunrisePreset.tilesIntensity.to,
                1 + this.presetsCorrection('zenitPreset','tilesIntensity',0)
            ),
            buildingIntensity: new Lerp(
                this.sunrisePreset.buildingIntensity.to,
                1 + this.presetsCorrection('zenitPreset','buildingIntensity',0)
            ),
            pointLightIntensity: new Lerp(0,0),
            buildingEmissiveIntensity: new Lerp(
                this.sunrisePreset.buildingEmissiveIntensity.to,
                0 + this.presetsCorrection('zenitPreset','buildingEmissiveIntensity',0)
            ),
            stars: new Lerp(0,0),
            sun: true,
            timeLine: this.dayInfo.afterSunrise - this.dayInfo.zenit
        };

        this.fromZenitToBeforeSunset = {
            turbidity: new Lerp(this.zenitPreset.turbidity.to,0.4),
            rayleigh: new Lerp(this.zenitPreset.rayleigh.to,0.351),
            mieCoefficient: new Lerp(this.zenitPreset.mieCoefficient.to,0.001),
            mieDirectionalG: new Lerp(this.zenitPreset.mieDirectionalG.to,0.999),
            directionalIntensity: new Lerp(
                this.zenitPreset.directionalIntensity.to,
                0.7 + this.presetsCorrection('fromZenitToBeforeSunset','directionalIntensity',0)
            ),
            directionalColor: new Color().setStyle(this.presetsCorrection('fromZenitToBeforeSunset','directionalColor','#ffe6c3'),LinearSRGBColorSpace),
            ambientIntensity: new Lerp(
                this.zenitPreset.ambientIntensity.to,
                0 + this.presetsCorrection('fromZenitToBeforeSunset','ambientIntensity',0)
            ),
            ambientColor: new Color().setStyle(this.presetsCorrection('fromZenitToBeforeSunset','ambientColor','#c2dbff'),LinearSRGBColorSpace),
            cloudsColor: new Color().setStyle(this.presetsCorrection('fromZenitToBeforeSunset','cloudsColor','#ffffff'),LinearSRGBColorSpace),
            seaColor: new Color().setStyle(this.presetsCorrection('fromZenitToBeforeSunset','seaColor','#43a0a8'),LinearSRGBColorSpace),
            seaWaveColor: new Color().setStyle(this.presetsCorrection('fromZenitToBeforeSunset','seaWaveColor','#ffffff'),LinearSRGBColorSpace),
            treeColor: new Color().setStyle(this.presetsCorrection('fromZenitToBeforeSunset','treeColor','#ffffff'),LinearSRGBColorSpace),
            osmLightMapIntensity: new Lerp(
                this.zenitPreset.osmLightMapIntensity.to,
                0 + this.presetsCorrection('fromZenitToBeforeSunset','osmLightMapIntensity',0)
            ),
            osmEnvMapIntensity: new Lerp(
                this.zenitPreset.osmEnvMapIntensity.to,
                1 + this.presetsCorrection('fromZenitToBeforeSunset','osmEnvMapIntensity',0)
            ),
            tilesIntensity: new Lerp(
                this.zenitPreset.tilesIntensity.to,
                1 + this.presetsCorrection('fromZenitToBeforeSunset','tilesIntensity',0)
            ),
            buildingIntensity: new Lerp(
                this.zenitPreset.buildingIntensity.to,
                1 + this.presetsCorrection('fromZenitToBeforeSunset','buildingIntensity',0)
            ),
            pointLightIntensity: new Lerp(0,0),
            buildingEmissiveIntensity: new Lerp(
                this.zenitPreset.buildingEmissiveIntensity.to,
                0 + this.presetsCorrection('fromZenitToBeforeSunsetPreset','buildingEmissiveIntensity',0)
            ),
            stars: new Lerp(0,0),
            sun: true,
            timeLine: this.dayInfo.zenit - this.dayInfo.beforeSunset
        };

        this.sunsetPreset = {
            turbidity: new Lerp(this.fromZenitToBeforeSunset.turbidity.to,2.4),
            rayleigh: new Lerp(this.fromZenitToBeforeSunset.rayleigh.to,1.96),
            mieCoefficient: new Lerp(this.fromZenitToBeforeSunset.mieCoefficient.to,0.001),
            mieDirectionalG: new Lerp(this.fromZenitToBeforeSunset.mieDirectionalG.to,0.999),
            directionalIntensity: new Lerp(
                this.fromZenitToBeforeSunset.directionalIntensity.to,
                0.35 + this.presetsCorrection('sunsetPreset','directionalIntensity',0)
            ),
            directionalColor: new Color().setStyle(this.presetsCorrection('sunsetPreset','directionalColor','#ffe6c3'),LinearSRGBColorSpace),
            ambientIntensity: new Lerp(
                this.fromZenitToBeforeSunset.ambientIntensity.to,
                0 + this.presetsCorrection('sunsetPreset','ambientIntensity',0)
            ),
            ambientColor: new Color().setStyle(this.presetsCorrection('sunsetPreset','ambientColor','#c2dbff'),LinearSRGBColorSpace),
            cloudsColor: new Color().setStyle(this.presetsCorrection('sunsetPreset','cloudsColor','#c5cbd6'),LinearSRGBColorSpace),
            seaColor: new Color().setStyle(this.presetsCorrection('sunsetPreset','seaColor','#37757a'),LinearSRGBColorSpace),
            seaWaveColor: new Color().setStyle(this.presetsCorrection('sunsetPreset','seaWaveColor','#FCD995'),LinearSRGBColorSpace),
            treeColor: new Color().setStyle(this.presetsCorrection('sunsetPreset','treeColor','#c7c7c7'),LinearSRGBColorSpace),
            osmLightMapIntensity: new Lerp(
                this.fromZenitToBeforeSunset.osmLightMapIntensity.to,
                0.5 + this.presetsCorrection('sunsetPreset','osmLightMapIntensity',0)
            ),
            osmEnvMapIntensity: new Lerp(
                this.fromZenitToBeforeSunset.osmEnvMapIntensity.to,
                1 + this.presetsCorrection('sunsetPreset','osmEnvMapIntensity',0)
            ),
            tilesIntensity: new Lerp(
                this.fromZenitToBeforeSunset.tilesIntensity.to,
                1 + this.presetsCorrection('sunsetPreset','tilesIntensity',0)
            ),
            buildingIntensity: new Lerp(
                this.fromZenitToBeforeSunset.buildingIntensity.to,
                1 + this.presetsCorrection('sunsetPreset','buildingIntensity',0)
            ),
            pointLightIntensity: new Lerp(
                this.fromZenitToBeforeSunset.pointLightIntensity.to,
                0 + this.presetsCorrection('sunsetPreset','pointLightIntensity',0)
            ),
            buildingEmissiveIntensity: new Lerp(
                this.fromZenitToBeforeSunset.buildingEmissiveIntensity.to,
                0.5 + this.presetsCorrection('sunsetPreset','buildingEmissiveIntensity',0)
            ),
            stars: new Lerp(
                0,
                0.2 + this.presetsCorrection('sunsetPreset','stars',0)
            ),
            sun: true,
            timeLine: this.dayInfo.beforeSunset - this.dayInfo.sunset
        };

        this.afterSunsetPreset = {
            turbidity: new Lerp(3.8,0.1),
            rayleigh: new Lerp(1.898,0.1),
            mieCoefficient: new Lerp(0,0.0005),
            mieDirectionalG: new Lerp(0.999,0.025),
            directionalIntensity: new Lerp(
                this.sunsetPreset.directionalIntensity.to,
                0 + this.presetsCorrection('afterSunsetPreset','directionalIntensity',0)
            ),
            directionalColor: new Color().copy(this.lightSaveSettings.directional.color),
            ambientIntensity: new Lerp(
                this.sunsetPreset.ambientIntensity.to,
                1 + this.presetsCorrection('afterSunsetPreset','ambientIntensity',0)
            ),
            ambientColor: new Color().setStyle(this.presetsCorrection('afterSunsetPreset','ambientColor','#000721'),LinearSRGBColorSpace),
            cloudsColor: new Color().setStyle(this.presetsCorrection('afterSunsetPreset','cloudsColor','#252934'),LinearSRGBColorSpace),
            seaColor: new Color().setStyle(this.presetsCorrection('afterSunsetPreset','seaColor','#163d3f'),LinearSRGBColorSpace),
            seaWaveColor: new Color().setStyle(this.presetsCorrection('afterSunsetPreset','seaWaveColor','#585858'),LinearSRGBColorSpace),
            treeColor: new Color().setStyle(this.presetsCorrection('afterSunsetPreset','treeColor','#474747'),LinearSRGBColorSpace),
            osmLightMapIntensity: new Lerp(
                this.sunsetPreset.osmLightMapIntensity.to,
                1 + this.presetsCorrection('afterSunsetPreset','osmLightMapIntensity',0)
            ),
            osmEnvMapIntensity: new Lerp(
                this.sunsetPreset.osmEnvMapIntensity.to,
                0.3 + this.presetsCorrection('afterSunsetPreset','osmEnvMapIntensity',0)
            ),
            tilesIntensity: new Lerp(
                this.sunsetPreset.tilesIntensity.to,
                0.3 + this.presetsCorrection('afterSunsetPreset','tilesIntensity',0)
            ),
            buildingIntensity: new Lerp(
                this.sunsetPreset.buildingIntensity.to,
                0.3 + this.presetsCorrection('afterSunsetPreset','buildingIntensity',0)
            ),
            pointLightIntensity: new Lerp(
                this.sunsetPreset.pointLightIntensity.to,
                1 + this.presetsCorrection('afterSunsetPreset','pointLightIntensity',0)
            ),
            buildingEmissiveIntensity: new Lerp(
                this.sunsetPreset.buildingEmissiveIntensity.to,
                1 + this.presetsCorrection('afterSunsetPreset','buildingEmissiveIntensity',0)
            ),
            stars: new Lerp(
                this.sunsetPreset.stars.to,
                0.5 + this.presetsCorrection('afterSunsetPreset','stars',0)
            ),
            sun: false,
            timeLine: this.dayInfo.sunset - this.dayInfo.afterSunset
        };

        this.dayEndPreset = {
            turbidity: new Lerp(this.afterSunsetPreset.turbidity.to, 0.1),
            rayleigh: new Lerp(this.afterSunsetPreset.rayleigh.to, 0.1),
            mieCoefficient: new Lerp(this.afterSunsetPreset.mieCoefficient.to,0.0005),
            mieDirectionalG: new Lerp(this.afterSunsetPreset.mieCoefficient.to,0.025),
            directionalIntensity: new Lerp(
                this.afterSunsetPreset.directionalIntensity.to,
                0 + this.presetsCorrection('dayEndPreset','directionalIntensity',0)
            ),
            directionalColor: new Color().copy(this.lightSaveSettings.directional.color),
            ambientIntensity: new Lerp(
                1 + this.presetsCorrection('dayEndPreset','ambientIntensity',0),
                1 + this.presetsCorrection('dayEndPreset','ambientIntensity',0)
            ),
            ambientColor: new Color().setStyle(this.presetsCorrection('dayEndPreset','ambientColor','#000721'),LinearSRGBColorSpace),
            cloudsColor: new Color().setStyle(this.presetsCorrection('dayEndPreset','cloudsColor','#252934'),LinearSRGBColorSpace),
            seaColor: new Color().setStyle(this.presetsCorrection('dayEndPreset','seaColor','#161835'),LinearSRGBColorSpace),
            seaWaveColor: new Color().setStyle(this.presetsCorrection('dayEndPreset','seaWaveColor','#6f6f6f'),LinearSRGBColorSpace),
            treeColor: new Color().setStyle(this.presetsCorrection('dayEndPreset','treeColor','#474747'),LinearSRGBColorSpace),
            osmLightMapIntensity: new Lerp(
                this.afterSunsetPreset.osmLightMapIntensity.to,
                1 + this.presetsCorrection('dayEndPreset','osmLightMapIntensity',0)
            ),
            osmEnvMapIntensity: new Lerp(
                this.afterSunsetPreset.osmEnvMapIntensity.to,
                0.3 + this.presetsCorrection('dayEndPreset','osmEnvMapIntensity',0)
            ),
            tilesIntensity: new Lerp(
                this.afterSunsetPreset.tilesIntensity.to,
                0.3 + this.presetsCorrection('dayEndPreset','tilesIntensity',0)
            ),
            buildingIntensity: new Lerp(
                this.afterSunsetPreset.buildingIntensity.to,
                0.3 + this.presetsCorrection('dayEndPreset','buildingIntensity',0)
            ),
            pointLightIntensity: new Lerp(
                this.afterSunsetPreset.pointLightIntensity.to,
                1.3 + this.presetsCorrection('dayEndPreset','pointLightIntensity',0)
            ),
            buildingEmissiveIntensity: new Lerp(
                this.afterSunsetPreset.buildingEmissiveIntensity.to,
                1.5 + this.presetsCorrection('dayEndPreset','buildingEmissiveIntensity',0)
            ),
            stars: new Lerp(1, 1),
            sun: false,
            timeLine: this.dayInfo.afterSunset - this.dayInfo.dayEnd,
        };

    }

    resetToDefault() {

        if (OrbitDayAndNight.usePresets) {
            this.reset = true;
            this.currentPresetCopy = {};
            Object.entries(this.currentPreset).forEach(([key,value]) => {
                if (typeof value === 'object') {
                    this.currentPresetCopy[key] = new Color().copy(value);
                } else {
                    this.currentPresetCopy[key] = value;
                }
            });
            this.currentPresetCopy.directionalLightPosition = new Vector3().copy(this.directionalLight.position);

            const tilesIntensityToValue =
                threeJsVariables.bundleConfig && threeJsVariables.bundleConfig.version === 1
                    ? threeJsVariables.bundleConfig.tiles.material.config.envMapIntensity ?
                    threeJsVariables.bundleConfig.tiles.material.config.envMapIntensity : 1
                    : 1;
            this.activePreset = {
                turbidity: new Lerp(0, 0),
                rayleigh: new Lerp(0, 0),
                mieCoefficient: new Lerp(0, 0),
                mieDirectionalG: new Lerp(0, 0),
                directionalIntensity: new Lerp(this.defaultLightSettings.directionalLightIntensity, this.defaultLightSettings.directionalLightIntensity),
                directionalColor: this.defaultLightSettings.directionalLightColor,
                ambientIntensity: new Lerp(this.defaultLightSettings.ambientIntensity,this.defaultLightSettings.ambientIntensity),
                ambientColor: this.defaultLightSettings.ambientColor,
                cloudsColor: new Color().setStyle('#ffffff',LinearSRGBColorSpace),
                seaColor: new Color().setStyle('#386F90',LinearSRGBColorSpace),
                seaWaveColor: new Color().setStyle('#6a9ebd',LinearSRGBColorSpace),
                treeColor: new Color().setStyle('#ffffff',LinearSRGBColorSpace),
                osmLightMapIntensity: new Lerp(0, 0),
                osmEnvMapIntensity: new Lerp(1, 1),
                tilesIntensity: new Lerp(1, tilesIntensityToValue),
                buildingIntensity: new Lerp(this.defaultLightSettings.buildingIntensity, this.defaultLightSettings.buildingIntensity),
                pointLightIntensity: new Lerp(0, 0),
                buildingEmissiveIntensity: new Lerp(0, 0),
                stars: new Lerp(0, 0),
                sun: true,
                timeLine: 0
            };

            this.alpha = 1;
            this.mergePresetToCurrent();

            this.directionalLight.position.copy(this.defaultLightSettings.directionalLightPosition);
            this.directionalLight.color.copy(this.defaultLightSettings.directionalLightColor);
            this.directionalLight.intensity = this.defaultLightSettings.directionalLightIntensity;
            this.ambientLight.intensity = this.defaultLightSettings.ambientIntensity;
            this.ambientLight.color.copy(this.defaultLightSettings.ambientColor);
            this.directionalLight.castShadow = getCastShadow();
            this.directionalLight.lookAt(new Vector3());
            this.secondDirectionalLight?.updateParams();
        } else {
            this.directionalLight.intensity = this.lightSaveSettings.directional.intensity;
            this.directionalLight.color.copy(this.lightSaveSettings.directional.color);
            this.directionalLight.position.copy(this.lightSaveSettings.directional.position);
            this.directionalLight.castShadow = getCastShadow();
            this.directionalLight.lookAt(new Vector3());
            this.secondDirectionalLight?.updateParams();

            // const time = this.#settings.time.split(':');
            // const hours = Number(time[0]);
            // const minutes = Number(time[1]);
            // this.currentTime = new Date(this.currentTime).setHours(hours,minutes);
            // this.updateTimesOfDay(this.currentTime,true);
            this.updateNightBuildings(1);
            Environment.setAlpha(1);
        }

        updateShadowMap();

        this.reset = false;
    }

    restorePreviouslyPreset() {
        if (OrbitDayAndNight.usePresets) {
            this.currentPreset = this.currentPresetCopy;
            this.directionalLight.position.copy(this.currentPresetCopy.directionalLightPosition);
            this.directionalLight.lookAt(0,0,0);
            this.#applyPreset();
            OSMEnv.updateLightMap(this.currentPreset.osmLightMapIntensity);
            OSMEnv.updateEnvMapIntensity(this.currentPreset.osmEnvMapIntensity);
            this.changeTilesIntensity(this.currentPreset.tilesIntensity);
            this.changeMainBuildingIntensity(this.currentPreset.buildingIntensity);
            this.updatePointsLight(this.currentPreset.pointLightIntensity);
            this.updateBuildingsEmissive();
        } else {
            const inputEvent = new Event('input');
            this.range.element.dispatchEvent(inputEvent);
        }

        const changeEvent = new Event('change');
        this.shadowSwitcher.checkbox.dispatchEvent(changeEvent);
    }

    rangeHandler(e,manualUpdate = false) {
        if (!OrbitDayAndNight.isActiveAndSelected) return;

        this.currentRangeValue = parseInt(e.target.value);
        if (isNaN(this.currentRangeValue)) return;

        this.currentRangeValue = clamp(this.currentRangeValue,this.minRangeValue,this.maxRangeValue);

        if (!manualUpdate) return;

        DayNightButton.scope?.setSwitcherState(this.currentRangeValue, this.dayInfo);

        this.showCurrentTimeBubble();

        if (OrbitDayAndNight.usePresets) {
            if (this.prevValue !== 0) {
                if (this.prevValue > this.currentRangeValue) {
                    this.alphaDirection = -1;
                } else {
                    this.alphaDirection = 1;
                }
            }

            this.prevValue = this.currentRangeValue;

            if (this.#debug) {
                this.normalizeTime.setTime(this.currentRangeValue);
                this.sunController.currentTime = `${this.normalizeTime.getHours()}:${this.normalizeTime.getMinutes()}`;
                this.gui.updateDisplay();
            }

            // Night
            if (this.currentRangeValue > this.dayInfo.dayStart && this.currentRangeValue < this.dayInfo.beforeSunrise) {
                this.activePreset = this.nightPreset;
                this.alpha = Math.min((this.dayInfo.dayStart - this.currentRangeValue) / this.activePreset.timeLine, 1.0);
                this.mergePresetToCurrent();
                // console.log('night');
            }
            // Before sunrise
            else if (this.currentRangeValue > this.dayInfo.beforeSunrise && this.currentRangeValue < this.dayInfo.sunrise) {
                this.activePreset = this.beforeSunrisePreset;
                this.alpha = Math.min((this.dayInfo.beforeSunrise - this.currentRangeValue) / this.activePreset.timeLine, 1.0);
                this.mergePresetToCurrent();
                // console.log('before');
            }

            // Sunrise
            else if (this.currentRangeValue > this.dayInfo.sunrise && this.currentRangeValue < this.dayInfo.afterSunrise) {
                this.activePreset = this.sunrisePreset;
                this.alpha = Math.min((this.dayInfo.sunrise - this.currentRangeValue) / this.activePreset.timeLine, 1.0);
                this.mergePresetToCurrent();
                // console.log('sunrise');
            }

            //To Zenit
            else if (this.currentRangeValue > this.dayInfo.afterSunrise && this.currentRangeValue <= this.dayInfo.zenit) {
                this.activePreset = this.zenitPreset;
                this.alpha = Math.min((this.dayInfo.afterSunrise - this.currentRangeValue) / this.activePreset.timeLine, 1.0);
                this.mergePresetToCurrent();
                // console.log('zenit');
            }

            // to before sunset

            else if (this.currentRangeValue > this.dayInfo.zenit && this.currentRangeValue < this.dayInfo.beforeSunset) {
                this.activePreset = this.fromZenitToBeforeSunset;
                this.alpha = Math.min((this.dayInfo.zenit - this.currentRangeValue) / this.activePreset.timeLine, 1.0);
                this.mergePresetToCurrent();
                // console.log('after zenit');
            }

            // Sunset
            else if (this.currentRangeValue > this.dayInfo.beforeSunset && this.currentRangeValue < this.dayInfo.sunset) {
                this.activePreset = this.sunsetPreset;
                this.alpha = Math.min((this.dayInfo.beforeSunset - this.currentRangeValue) / this.activePreset.timeLine, 1.0);
                this.mergePresetToCurrent();
                // console.log('sunset');
            }

            // After sunset
            else if (this.currentRangeValue > this.dayInfo.sunset && this.currentRangeValue < this.dayInfo.afterSunset) {
                this.activePreset = this.afterSunsetPreset;
                this.alpha = Math.min((this.dayInfo.sunset - this.currentRangeValue) / this.activePreset.timeLine, 1.0);
                this.mergePresetToCurrent();
                // console.log('after sunset');
            }

            // Night
            else if (this.currentRangeValue > this.dayInfo.afterSunset && this.currentRangeValue < this.dayInfo.dayEnd) {
                this.activePreset = this.dayEndPreset;
                this.alpha = Math.min((this.dayInfo.afterSunset - this.currentRangeValue) / this.activePreset.timeLine, 1.0);
                this.mergePresetToCurrent();
                // console.log('night');
            }
        } else {

            this.alpha = 1;
            const oneHour = 3600000;
            Environment.setTime(this.currentRangeValue);
            Environment.setDayTime(this.dayInfo);

            if (this.currentRangeValue < this.dayInfo.sunrise + oneHour) {
                this.alpha = 1 - clamp(((this.dayInfo.sunrise + oneHour - this.currentRangeValue) / (this.dayInfo.sunrise + oneHour - this.dayInfo.sunrise)),0.0,1.0);
            } else if (this.currentRangeValue > this.dayInfo.sunset - oneHour) {
                this.alpha = 1 - clamp(((this.currentRangeValue - (this.dayInfo.sunset - oneHour)) / (this.dayInfo.sunset - (this.dayInfo.sunset - oneHour))),0.0,1.0);
            }
            this.directionalLight.intensity = this.lightSaveSettings.directional.intensity * this.alpha;
            Environment.setAlpha(this.alpha);
            this.updateNightBuildings(this.alpha);
            // projectMode.AirMode.updateAlpha(this.alpha);

            // OSMEnv.updateLightMap(1.0 - this.alpha);
        }


        //LightPosition
        if (this.currentRangeValue > this.dayInfo.sunrise && this.currentRangeValue < this.dayInfo.sunset) {

            const value = Math.min((this.currentRangeValue - this.dayInfo.sunrise) / this.sunTimePath, 1.0);
            const pos = this.curve.getPoint(value);

            OrbitDayAndNight.sky?.material.uniforms['sunPosition'].value.copy(pos);
            // this.water.material.uniforms.sunDirection.value.copy(pos).normalize();
            // OrbitDayAndNight.lens.position.copy(pos);
            this.directionalLight.position.copy(pos);
            this.directionalLight.lookAt(0,0,0);
            this.secondDirectionalLight?.updateParams();

        } else if (this.currentRangeValue > this.dayInfo.sunset) {

            const value = Math.min((this.currentRangeValue - this.dayInfo.sunset) / (this.dayInfo.dayEnd - this.dayInfo.sunset), 1.0);
            const pos = this.curve.getPoint(1);
            const sunPos = this.curveAfterSunset.getPoint(value);
            
            if (OrbitDayAndNight.sky) {
                OrbitDayAndNight.sky.shaderVersion === '1.0' && sunPos.copy(pos);
                OrbitDayAndNight.sky.material.uniforms['sunPosition'].value.copy(sunPos);
            }

            
            // this.water.material.uniforms.sunDirection.value.copy(pos).normalize();
            // OrbitDayAndNight.lens.position.copy(pos);
            this.directionalLight.position.copy(pos);
            this.directionalLight.lookAt(0,0,0);
            this.secondDirectionalLight?.updateParams();
        } else if (this.currentRangeValue < this.dayInfo.sunrise) {

            const value = Math.min((this.currentRangeValue - this.dayInfo.dayStart) / (this.dayInfo.sunrise - this.dayInfo.dayStart), 1.0);
            const pos = this.curve.getPoint(0);
            const sunPos = this.curveBeforeSunrise.getPoint(value);
            
            if (OrbitDayAndNight.sky) {
                OrbitDayAndNight.sky.shaderVersion === '1.0' && sunPos.copy(pos);
                OrbitDayAndNight.sky.material.uniforms['sunPosition'].value.copy(sunPos);
            }
            // this.water.material.uniforms.sunDirection.value.copy(pos).normalize();
            // OrbitDayAndNight.lens.position.copy(pos);
            this.directionalLight.position.copy(pos);
            this.directionalLight.lookAt(0,0,0);
            this.secondDirectionalLight?.updateParams();
        }

        OrbitDayAndNight.sky?.shaderVersion === '2.0' && OrbitDayAndNight.sky.updateUniforms();

        if (this.shadowSwitcher.checkbox.checked) {
            updateShadowMap();
        }
    }

    changeTilesIntensity(alpha) {
        setTilesEnvMapIntensity(alpha);

        if (threeJsVariables.tilesRenderer) {
            threeJsVariables.tilesRenderer.forEachLoadedModel((scene,tile)=>{
                scene.traverse(child =>{
                    if (child.isMesh){
                        if (child.material.type === 'MeshBasicMaterial') {
                            child.material.color.r = alpha;
                            child.material.color.g = alpha;
                            child.material.color.b = alpha;
                        } else {
                            child.material.envMapIntensity = alpha;
                        }

                        child.material.needsUpdate = true;
                    }
                });
            });
        } else {
            threeJsVariables.environment?.traverse(el => {
               if (el.isMesh) {
                   el.material.envMapIntensity = alpha;
               }
            });
        }
    }

    changeMainBuildingIntensity(intensity) {
        const buildings = Buildings.getBuildingsArray();

        // buildings.forEach(building => {
        //    const material = building.instances[0].material;
        //        if (Array.isArray(material)) {
        //            material.map(subMaterial => subMaterial.envMapIntensity = intensity);
        //        } else {
        //            material.envMapIntensity = intensity;
        //        }
        // });

        if (this.reset) {
            buildings.forEach(building => {
                const material = building.instances[0].material;
                if (Array.isArray(material)) {
                    material.map((subMaterial) =>
                        subMaterial.envMapIntensity = subMaterial.userData.defaultSettings
                            ? subMaterial.userData.defaultSettings.envMapIntensity
                            : this.defaultLightSettings.buildingIntensity);
                } else {
                    material.envMapIntensity =
                        material.userData.defaultSettings
                            ? material.userData.defaultSettings.envMapIntensity
                            : this.defaultLightSettings.buildingIntensity;
                }
                material.needsUpdate = true;
            });

            sceneObjectsGroup.traverse(el => {
                if (el.isMesh) {
                    const material = el.material;
                    if (Array.isArray(material)) {
                        material.map((subMaterial,index) =>
                            subMaterial.envMapIntensity = subMaterial.userData.defaultSettings
                                ? subMaterial.userData.defaultSettings.envMapIntensity
                                : this.defaultLightSettings.sceneObjects[el.name][index]);
                    } else {
                        material.envMapIntensity =
                            material.userData.defaultSettings
                                ? material.userData.defaultSettings.envMapIntensity
                                : this.defaultLightSettings.sceneObjects[el.name] ;
                    }
                }
            });
        } else {
            buildings.forEach(building => {
                const material = building.instances[0].material;
                if (Array.isArray(material)) {
                    material.map(subMaterial => subMaterial.envMapIntensity = intensity);
                } else {
                    material.envMapIntensity = intensity;
                }
            });

            sceneObjectsGroup.traverse(el => {
                if (el.isMesh) {
                    const material = el.material;
                    if (Array.isArray(material)) {
                        material.map(subMaterial => subMaterial.envMapIntensity = intensity);
                    } else {
                        material.envMapIntensity = intensity;
                    }
                }
            });
        }
    }

    switcherHandler(e) {
        if (!OrbitDayAndNight.isActiveAndSelected) return;

        // if (this.manualTime) {
        //     this.currentTime = this.manualTime;
        //
        //     this.updateTimesOfDay(this.currentTime,true);
        //     this.range.element.value = this.currentTime;
        //     return;
        // }

        switch (e.target.checked) {
            case true:

                const time = {value: parseInt(this.range.element.value)};

                gsap.to(time,{
                    value: this.currentTime,
                    duration: .7,
                    onStart: () => {
                        this.currentTimeToggle(false);
                        this.range.classToggle('disabled');
                    },
                    onUpdate: () => {
                        this.range.element.value = time.value;
                        this.updateTimesOfDay(time.value,true);
                    },
                    onComplete: () => {
                        this.currentTimeToggle(true);
                    }
                });

                break;
            case false:
                this.range.removeClass('disabled');
                this.currentTimeToggle(false);
                this.showCurrentTimeBubble();
                break;
        }
    }

    refreshTime() {
        if (!OrbitDayAndNight.isActiveAndSelected) return;

        this.currentTime = this.getCurrentTimeMilliseconds;

        this.currentTimeToggle() && this.updateTimesOfDay(this.getCurrentTimeMilliseconds);
    }

    updateTimesOfDay(time, manualUpdate = false) {
        if (!OrbitDayAndNight.isActiveAndSelected) return;

        this.rangeHandler({target: {value: time}},manualUpdate);
    }

    #addDebug() {
        if (!this.#debug) return;

        this.sunController = {
            turbidity: 5.2,
            rayleigh: 0.831,
            mieCoefficient: 0.001,
            mieDirectionalG: 0.999,
            exposure: threeJsVariables.renderer.toneMappingExposure,
            toneMapping: 'None',
            colorSpace: 'Linear',
            radius: 3000,
            currentTime: '',
            directionalLight: {
                intensity: this.directionalLight.intensity,
                color: '#' + this.directionalLight.color.getHexString()
            },
            ambientLight: {
                intensity: this.ambientLight.intensity,
                color: '#' + this.ambientLight.color.getHexString()
            },
            shadows: {
                near: this.directionalLight.shadow.camera.near,
                far: this.directionalLight.shadow.camera.far,
                size: this.directionalLight.shadow.camera.top
            },
            // fog: '#' + this.fog.color.getHexString(),
            pointLight: {
                intensity: 1,
                angle: 1,
                penumbra: 1,
                decay: 1,
                distance: 100,
            },
            water: {
                alpha: 1,
                size: 1,
                distortionScale: 20,
                sunColor: '#ffffff',
                waterColor: '#001e0f'
            }
        };


        this.normalizeTime = new Date(this.currentTime);
        this.gui = new dat.GUI();
        this.gui.width = 400;
        this.gui.domElement.style.position = 'absolute';
        this.gui.domElement.style.right = '260px';
        this.gui.domElement.style.top = '0';
        const sunFolder = this.gui.addFolder('Sun');
        const renderer = this.gui.addFolder('Renderer');
        const lights = this.gui.addFolder('Lights');
        const directional = lights.addFolder('Directional Light');
        const ambient = lights.addFolder('Ambient Light');
        const shadows = lights.addFolder('Shadows');

        const nightLight = this.gui.addFolder('Night lights');
        nightLight.add(this.sunController.pointLight, 'intensity', 0.0,10, 0.01).onChange(this.#updateSunSettings.bind(this) );
        nightLight.add(this.sunController.pointLight, 'decay', 0.0, 10.0, 0.01).onChange(this.#updateSunSettings.bind(this) );
        nightLight.add(this.sunController.pointLight, 'distance', 0.0, 10000, 1).onChange(this.#updateSunSettings.bind(this) );
        nightLight.add(this.sunController.pointLight, 'angle', 0.0, Math.PI / 2, 0.01).onChange(this.#updateSunSettings.bind(this) );
        nightLight.add(this.sunController.pointLight, 'penumbra', 0.0, 10, 0.01).onChange(this.#updateSunSettings.bind(this) );

        const input = document.createElement('input');
        input.type = 'file';
        input.addEventListener('change', inputHandler);

        const textureLoader = new TextureLoader();

        function inputHandler (e) {
            const file = this.files[0];

            const fileReader = new FileReader();
            fileReader.onload = function(e) {
                const res = e.target.result;

                textureLoader.load(res, (texture) => {

                    const buildings = Buildings.getBuildingsArray();
                    buildings.forEach(building => {
                        building.instances[0].material.map = texture;
                        building.instances[0].material.needsUpdate = true;
                    });
                });
            };
            fileReader.readAsDataURL(file);
        }

        const textures = this.gui.addFolder('Textures');
        const map = {
            map: () => {
                input.click();
            }
        };

        textures.add(map,'map');

        // const water = gui.addFolder('Water');
        // water.add(this.sunController.water, 'alpha', 0, 5, 0.001).onChange(this.#updateSunSettings.bind(this) );
        // water.add(this.sunController.water, 'size', 0, 5000, 1).onChange(this.#updateSunSettings.bind(this) );
        // water.add(this.sunController.water, 'distortionScale', 0, 100, 1).onChange(this.#updateSunSettings.bind(this) );
        // water.addColor(this.sunController.water,'sunColor').onChange(this.#updateSunSettings.bind(this) );
        // water.addColor(this.sunController.water,'waterColor').onChange(this.#updateSunSettings.bind(this) );


        sunFolder.add( this.sunController, 'turbidity', 0.0, 20.0, 0.1 ).onChange( this.#updateSunSettings.bind(this) );
        sunFolder.add( this.sunController, 'rayleigh', 0.0, 4, 0.001 ).onChange( this.#updateSunSettings.bind(this));
        sunFolder.add( this.sunController, 'mieCoefficient', 0.0, 0.1, 0.001 ).onChange( this.#updateSunSettings.bind(this) );
        sunFolder.add( this.sunController, 'mieDirectionalG', 0.0, 1, 0.001 ).onChange( this.#updateSunSettings.bind(this) );
        // sunFolder.add( this.sunController, 'radius', 0.0, 3000, 1 ).onChange( this.#updateSunSettings.bind(this) );
        // sunFolder.addColor( this.sunController, 'fog').onChange(this.#updateSunSettings.bind(this));

        // renderer.add(this.sunController, 'toneMapping',['None','Linear','Reinhard','Cineon','Filmic']).onChange(this.#updateSunSettings.bind(this) );
        // renderer.add(this.sunController, 'outputEncoding',['Linear','sRGB']).onChange(this.#updateSunSettings.bind(this) );
        // renderer.add( this.sunController, 'exposure', 0.0, 5, 0.0001).onChange( this.#updateSunSettings.bind(this) );

        directional.add(this.currentPreset, 'directionalIntensity',0.0, 10, 0.01).onChange(this.#updateSunSettings.bind(this));
        directional.addColor(this.currentPreset,'directionalColor').onChange(this.#updateSunSettings.bind(this));

        ambient.add(this.currentPreset, 'ambientIntensity', 0.0, 10, 0.01).onChange(this.#updateSunSettings.bind(this));
        ambient.addColor(this.currentPreset, 'ambientColor').onChange(this.#updateSunSettings.bind(this));

        const targetCurrentTime = this.gui.add(this.sunController,'currentTime');
        targetCurrentTime.domElement.firstElementChild.setAttribute('disabled', 'true');

        // shadows.add(this.sunController.shadows,'near',0, 2000,1).onChange(this.#updateSunSettings.bind(this));
        // shadows.add(this.sunController.shadows,'far',0, 2000,1).onChange(this.#updateSunSettings.bind(this));
        // shadows.add(this.sunController.shadows,'size',0,1000,1).onChange(this.#updateSunSettings.bind(this));

        this.directionalLightShadowCamera = new CameraHelper(this.directionalLight.shadow.camera);

        threeJsVariables.scene.add(this.directionalLightShadowCamera);

        // Add sun path
        const geometry = new BufferGeometry().setFromPoints(this.points);
        const material = new LineBasicMaterial( { color: 0xff0000 } );
        const sunPath = new Line( geometry, material );

        threeJsVariables.scene.add(sunPath);

        threeJsVariables.orbitControls.maxDistance = Infinity;
        threeJsVariables.trackballControls.maxDistance = Infinity;

        const stats = new Stats();
        stats.dom.style.top = '20px';
        stats.dom.style.left = '7%';

        threeJsVariables.stats = stats;

        document.body.insertAdjacentElement('afterbegin', stats.dom);
    }

    #applyPreset() {

        if (!OrbitDayAndNight.usePresets) return;

        const uniforms = OrbitDayAndNight.sky.material.uniforms;

        uniforms[ 'turbidity' ].value = this.currentPreset.turbidity;
        uniforms[ 'rayleigh' ].value = this.currentPreset.rayleigh;
        uniforms[ 'mieCoefficient' ].value = this.currentPreset.mieCoefficient;
        uniforms[ 'mieDirectionalG' ].value = this.currentPreset.mieDirectionalG;
        uniforms[ 'stars' ].value = this.currentPreset.stars;
        uniforms[ 'clouds' ].value.copy(this.currentPreset.cloudsColor);

        this.directionalLight.intensity = this.currentPreset.directionalIntensity * Math.PI;
        this.directionalLight.color.copy(this.currentPreset.directionalColor);
        this.secondDirectionalLight?.updateParams();
        this.ambientLight.color.copy(this.currentPreset.ambientColor);
        this.ambientLight.intensity = this.currentPreset.ambientIntensity * Math.PI;
        // OrbitDayAndNight.lens.visible = this.currentPreset.sun;
        OSMEnv.updateSeaColor(this.currentPreset.seaColor,this.currentPreset.seaWaveColor);

        const trees = OSMEnv.osm.getObjectByName('Trees');
        if (trees) {
            trees.traverse(el => {
                if (el.isPoints) {
                    el.material.color.copy(this.currentPreset.treeColor);
                }
            });
        }


    }

    #updateSunSettings() {
        const uniforms = OrbitDayAndNight.sky.material.uniforms;

        uniforms[ 'turbidity' ].value = this.sunController.turbidity;
        uniforms[ 'rayleigh' ].value = this.sunController.rayleigh;
        uniforms[ 'mieCoefficient' ].value = this.sunController.mieCoefficient;
        uniforms[ 'mieDirectionalG' ].value = this.sunController.mieDirectionalG;


        // const waterUniforms = this.water.material.uniforms;
        //
        // waterUniforms.alpha.value = this.sunController.water.alpha;
        // waterUniforms.size.value = this.sunController.water.size;
        // waterUniforms.distortionScale.value = this.sunController.water.distortionScale;
        // waterUniforms.sunColor.value.set(this.sunController.water.sunColor);
        // waterUniforms.waterColor.value.set(this.sunController.water.waterColor);

        switch (this.sunController.toneMapping) {
            case 'None':
                threeJsVariables.renderer.toneMapping = NoToneMapping;
                break;
            case 'Linear':
                threeJsVariables.renderer.toneMapping = LinearToneMapping;
                break;
            case 'Reinhard':
                threeJsVariables.renderer.toneMapping = ReinhardToneMapping;
                break;
            case 'Cineon':
                threeJsVariables.renderer.toneMapping = CineonToneMapping;
                break;
            case 'Filmic':
                threeJsVariables.renderer.toneMapping = ACESFilmicToneMapping;
                break;
        }

        switch (this.sunController.colorSpace) {
            case 'Linear':
                    threeJsVariables.renderer.colorSpace = LinearSRGBColorSpace;
                break;
            case 'sRGB':
                    threeJsVariables.renderer.colorSpace = SRGBColorSpace;
                break;
        }

        // threeJsVariables.renderer.toneMappingExposure = this.sunController.exposure;
        // this.sky.scale.setScalar(this.sunController.radius);
        // this.fog.color.set(this.sunController.fog);

        //Lights
        // this.directionalLight.intensity = this.currentPreset.directionalIntensity;
        // this.directionalLight.color.copy(this.currentPreset.directionalColor);
        //
        // this.ambientLight.intensity = this.currentPreset.ambientIntensity;
        // this.ambientLight.color.copy(this.currentPreset.ambientColor);

        //Shadows
        this.directionalLight.shadow.camera.near = this.sunController.shadows.near;
        this.directionalLight.shadow.camera.far = this.sunController.shadows.far;
        this.directionalLight.shadow.camera.right = this.sunController.shadows.size;
        this.directionalLight.shadow.camera.left = - this.sunController.shadows.size;
        this.directionalLight.shadow.camera.top	= this.sunController.shadows.size;
        this.directionalLight.shadow.camera.bottom = - this.sunController.shadows.size;

        this.directionalLightShadowCamera.update();
        this.directionalLight.shadow.camera.updateProjectionMatrix();

        this.pointLightGroup.traverse(el => {
           if (el.name === 'building light') {
               el.intensity = this.sunController.pointLight.intensity;
               el.distance = this.sunController.pointLight.distance;
               el.decay = this.sunController.pointLight.decay;
               el.penumbra = this.sunController.pointLight.penumbra;
               el.angle = this.sunController.pointLight.angle;
               // el.power = this.sunController.pointLight.power;
           }
        //
        });



    }

    mergePresetToCurrent() {
        this.currentPreset.turbidity = this.activePreset.turbidity.lerp(this.alpha);
        this.currentPreset.rayleigh = this.activePreset.rayleigh.lerp(this.alpha);
        this.currentPreset.mieDirectionalG = this.activePreset.mieDirectionalG.lerp(this.alpha);
        this.currentPreset.mieCoefficient = this.activePreset.mieCoefficient.lerp(this.alpha);
        this.currentPreset.directionalIntensity = this.activePreset.directionalIntensity.lerp(this.alpha);
        this.currentPreset.ambientIntensity = this.activePreset.ambientIntensity.lerp(this.alpha);
        this.currentPreset.pointLightIntensity = this.activePreset.pointLightIntensity.lerp(this.alpha);
        this.currentPreset.buildingEmissiveIntensity = this.activePreset.buildingEmissiveIntensity.lerp(this.alpha);

        if (this.alphaDirection < 0) {
            this.currentPreset.seaColor.lerp(this.activePreset.seaColor,1 - this.alpha);
            this.currentPreset.seaWaveColor.lerp(this.activePreset.seaWaveColor, 1 - this.alpha);
            this.currentPreset.cloudsColor.lerp(this.activePreset.cloudsColor,1 - this.alpha);
            this.currentPreset.directionalColor.lerp(this.activePreset.directionalColor,1 - this.alpha);
            this.currentPreset.ambientColor.lerp(this.activePreset.ambientColor,1 - this.alpha);
            this.currentPreset.treeColor.lerp(this.activePreset.treeColor,1 - this.alpha);

        } else {
            this.currentPreset.directionalColor.lerp(this.activePreset.directionalColor,this.alpha);
            this.currentPreset.ambientColor.lerp(this.activePreset.ambientColor,this.alpha);
            this.currentPreset.seaColor.lerp(this.activePreset.seaColor,this.alpha);
            this.currentPreset.seaWaveColor.lerp(this.activePreset.seaWaveColor,this.alpha);
            this.currentPreset.cloudsColor.lerp(this.activePreset.cloudsColor,this.alpha);
            this.currentPreset.treeColor.lerp(this.activePreset.treeColor,this.alpha);
        }

        this.currentPreset.stars = this.activePreset.stars.lerp(this.alpha);
        this.currentPreset.sun = this.activePreset.sun;

        this.#applyPreset();

        // if (this.activePreset.osmLightMapIntensity) {
            this.currentPreset.osmLightMapIntensity = this.activePreset.osmLightMapIntensity.lerp(this.alpha);
            OSMEnv.updateLightMap(this.currentPreset.osmLightMapIntensity);
        // }

        // if (this.activePreset.osmEnvMapIntensity) {
            this.currentPreset.osmEnvMapIntensity = this.activePreset.osmEnvMapIntensity.lerp(this.alpha);
            OSMEnv.updateEnvMapIntensity(this.currentPreset.osmEnvMapIntensity);
        // }

        // if (this.activePreset.tilesIntensity) {
            this.currentPreset.tilesIntensity = this.activePreset.tilesIntensity.lerp(this.alpha);
            this.changeTilesIntensity(this.currentPreset.tilesIntensity)
        // }

        // if (this.activePreset.buildingIntensity) {
            this.currentPreset.buildingIntensity = this.activePreset.buildingIntensity.lerp(this.alpha);
            this.changeMainBuildingIntensity(this.currentPreset.buildingIntensity);
        // }

        // if (Weather.selectedWeather.id === 'clouds') {
        //     const clouds = Weather.effects['clouds'].particles;
        //
        //     clouds.forEach(cloud => {
        //         cloud.material.color.copy(this.currentPreset.cloudsColor);
        //     })
        // }

        this.updatePointsLight(this.currentPreset.pointLightIntensity);
        this.updateBuildingsEmissive();

    }

    static addSun(params = {}) {
        if (!params.hasOwnProperty('ver')) params.ver = '1.0';

        const fromConfig = config.features.dayNight.settings.shaderVersion;
        if (fromConfig) params.ver = fromConfig;

        switch (params.ver) {
            case '2.0':
                return OrbitDayAndNight.addNewSun(params);
            default:
                return OrbitDayAndNight.addOldSun(params);
        }
    }

    static addNewSun(params = {}) {
        const sunGroup = new Group();
        sunGroup.name = 'Box';
        OrbitDayAndNight.sky = new OrbitSky(params);
        OrbitDayAndNight.sky.shaderVersion = '2.0';
        sunGroup.add(OrbitDayAndNight.sky);

        // OrbitDayAndNight.lens = new Lensflare();
        // OrbitDayAndNight.lens.addElement(new LensflareElement(OrbitDayAndNight.preloaded.lensFlare,60,0.6));
        // OrbitDayAndNight.lens.addElement(new LensflareElement(OrbitDayAndNight.preloaded.lensFlare,70,0.7));
        // OrbitDayAndNight.lens.addElement(new LensflareElement(OrbitDayAndNight.preloaded.lensFlare,120,0.9));
        // OrbitDayAndNight.lens.addElement(new LensflareElement(OrbitDayAndNight.preloaded.lensFlare,70,1));
        //
        // sunGroup.add(OrbitDayAndNight.lens);

        return sunGroup;
    }

    static addOldSun(params = {}) {
        const {clouds, sky} = config.features.dayNight.settings;
        const sunGroup = new Group();
        sunGroup.name = 'Box';
        const turbidity = params.turbidity || 2;
        const rayleigh = params.rayleigh || 1;
        const mieCoefficient = params.mieCoefficient || 0.005;
        const mieDirectionalG = params.mieDirectionalG || 0.8;
        const sunPosition = this.points?.length > 0
            ? this.points[0].clone()
            : new Vector3(0,100,100);
        const useTime = params.useTime || false;

        Sky.SkyShader.uniforms = {
            'turbidity': { value: turbidity },
            'rayleigh': { value: rayleigh },
            'mieCoefficient': { value: mieCoefficient },
            'mieDirectionalG': { value: mieDirectionalG },
            'sunPosition': { value: sunPosition },
            'up': { value: new Vector3( 0, 1, 0 ) },
            'stars': {value: 0.0},
            'clouds': {value: new Color().setStyle('#ffffff',LinearSRGBColorSpace)},
            'showClouds': {value: false},
            'useTime': {value: useTime},
            'cloudsScale': {value: clouds.scale },
            'cloudsDark': {value: clouds.dark},
            'cloudsLight': {value: clouds.light},
            'cloudsCover': {value: clouds.cover},
            'cloudsAlpha': {value: clouds.alpha},
            'cloudsTint': {value: clouds.tint},
            'sunriseTime': {value: 0.0},
            'sunsetTime': {value: 0.0},
            'currentTime': {value: 0.0},
            'dayStartTime': {value: 0.0},
            'dayEndTime': {value: 0.0},
            'uTime': {value: 0.0},
            'cloudsSpeed': {value: 0.002}
        };

        Sky.SkyShader.vertexShader = skyVertex;
        Sky.SkyShader.fragmentShader = skyFragment;

        const scale = sky?.scale || 3000;
        OrbitDayAndNight.sky = new Sky();
        OrbitDayAndNight.sky.shaderVersion = '1.0';
        OrbitDayAndNight.sky.material.transparent = true;
        OrbitDayAndNight.sky.material.needsUpdate = true;
        OrbitDayAndNight.sky.geometry = new SphereGeometry(1,64,64);
        OrbitDayAndNight.sky.scale.setScalar(scale);

        sunGroup.add(OrbitDayAndNight.sky);

        // OrbitDayAndNight.lens = new Lensflare();
        // OrbitDayAndNight.lens.addElement(new LensflareElement(OrbitDayAndNight.preloaded.lensFlare,60,0.6));
        // OrbitDayAndNight.lens.addElement(new LensflareElement(OrbitDayAndNight.preloaded.lensFlare,70,0.7));
        // OrbitDayAndNight.lens.addElement(new LensflareElement(OrbitDayAndNight.preloaded.lensFlare,120,0.9));
        // OrbitDayAndNight.lens.addElement(new LensflareElement(OrbitDayAndNight.preloaded.lensFlare,70,1));
        //
        // sunGroup.add(OrbitDayAndNight.lens);

        return sunGroup;
    }

    static get isActive() {
        return OrbitDayAndNight.active;
    }

    static get isActiveAndSelected() {
        return !!(OrbitDayAndNight.active && OrbitDayAndNight.osmSelected);
    }

    static get hasBackgroundToggle() {
        return config.features.dayNight?.hasOwnProperty('checked');
    }

    static toggle(action) {
        if (!OrbitDayAndNight.isActiveAndSelected || BmbyMode.active)
            return;

        OSMEnv.toggle(action);
        Environment.toggle3D(action)

        if (config.resources.background.cylinderBg !== 'sky') {
            if (OrbitDayAndNight.sky) {
                OrbitDayAndNight.sky.visible = action === 'show';
            }
        }
        
        // const tl = gsap.timeline({
        //     paused: true,
        //     onStart: () => {
        //
        //     },
        //     onComplete: () => {
        //
        //     }
        // });
    }

    // addSun() {
    //
    //     Sky.SkyShader.uniforms = {
    //         'turbidity': { value: 2 },
    //         'rayleigh': { value: 1 },
    //         'mieCoefficient': { value: 0.005 },
    //         'mieDirectionalG': { value: 0.8 },
    //         'sunPosition': { value: new Vector3() },
    //         'up': { value: new Vector3( 0, 1, 0 ) },
    //         'stars': {value: 0.0}
    //     };
    //
    //     Sky.SkyShader.fragmentShader = skyFragment;
    //
    //     this.sky = new Sky();
    //     this.sky.scale.setScalar(4500);
    //
    //     threeJsVariables.scene.add(this.sky);
    //
    //     this.sun = new Lensflare();
    //     this.sun.addElement(new LensflareElement(OrbitDayAndNight.preloaded.lensFlare,60,0.6));
    //     this.sun.addElement(new LensflareElement(OrbitDayAndNight.preloaded.lensFlare,70,0.7));
    //     this.sun.addElement(new LensflareElement(OrbitDayAndNight.preloaded.lensFlare,120,0.9));
    //     this.sun.addElement(new LensflareElement(OrbitDayAndNight.preloaded.lensFlare,70,1));
    //
    //     threeJsVariables.scene.add(this.sun);
    // }

    addPointLight() {

        if (isMobile) return;

        this.pointLightGroup = new Group();
        this.pointLightGroup.name = 'night lights';
        //
        // const dracoLoader = new DRACOLoader().setDecoderPath('js/libs/gltf/');
        // const loader = new GLTFLoader().setDRACOLoader(dracoLoader);
        //
        // const url = `${config.cdnRepository}/${config.projectFolder}/resources/points/`;
        // const sprite = this.textureLoader.load(url + 'lightSprite.png');
        //
        // loader.load(url + 'lightPoints.glb',points => {
        //
        //     console.log(points);
        //     const positions = [];
        //     const geometry = new BufferGeometry();
        //
        //     points.scene.traverse(point => {
        //         if (point.name.startsWith('Dummy')) {
        //             const {x,y,z} = point.position;
        //             positions.push(x,y,z);
        //         }
        //     });
        //
        //     geometry.setAttribute( 'position', new Float32BufferAttribute( positions, 3 ));
        //
        //     const material = new PointsMaterial({
        //         size: 10,
        //         sizeAttenuation: true,
        //         map: sprite,
        //         transparent: true,
        //         depthWrite:false
        //     });
        //
        //     const particles = new Points(geometry,material);
        //     particles.name = 'city light';
        //     particles.rotation.y = 1.37;
        //     particles.visible = false;
        //
        //     this.pointLightGroup.add(particles);
        // });

        const highwaySettings = config.features.publicLayers?.layers.find(el => el.type === 'highway');
        this.pointLightGroup.position.set(0, highwaySettings.position.y,0);
        // this.pointLightGroup.visible = true;

        const target = new Object3D();
        const v = new Vector3(1,0,0);
        const axis = new Vector3(0,1,0).normalize();
        const distance = 800;
        const distanceVector = new Vector3();

        [0,Math.PI / 2, -Math.PI / 2, Math.PI].forEach(angel => {
            distanceVector.copy(v).setLength(distance).applyAxisAngle(axis,angel);
            const light = new SpotLight(new Color().setStyle('#ffef8f',LinearSRGBColorSpace),0,2000,1.57,1,0);
            light.castShadow = false;
            light.name = 'env light';
            light.position.copy(distanceVector);
            light.target = target;

            this.pointLightGroup.add(light);
        });


        //Add buildings light
        const projectBuildings = threeJsVariables.scene.getObjectByName('Project Buildings');
        const labels = threeJsVariables.scene.getObjectByName('buildingsLabelsGroup');
        const projectSize = new Box3().setFromObject(projectBuildings).getSize(new Vector3());
        const projectCenter = new Box3().setFromObject(projectBuildings).getCenter(new Vector3());
        const y = labels?.children[0]?.position.y * 1.5 || 300;
        const projectLength = projectSize.length();
        const buildingTarget = new Object3D();
        buildingTarget.position.set(projectCenter.x,y,projectCenter.z);
        buildingTarget.name = 'building light target';

        const buildingVec = new Vector3().copy(projectCenter).setY(0).normalize();
        this.pointLightGroup.add(buildingTarget);

        [0,MathUtils.degToRad(120),MathUtils.degToRad(240)].forEach(angel => {
           distanceVector.copy(buildingVec).setLength(projectLength).applyAxisAngle(axis,angel);
           const buildingLight = new SpotLight(new Color().setStyle('#fcf1b1',LinearSRGBColorSpace),0,y,0.73,1,0);
           buildingLight.castShadow = false;
           buildingLight.name = 'building light';
           buildingLight.position.copy(distanceVector);
           buildingLight.target = buildingTarget;
           this.pointLightGroup.add(buildingLight);
        });

        threeJsVariables.scene.add(this.pointLightGroup);
    }

    updatePointsLight(intensity) {

        if (isMobile) return;

        this.pointLightGroup.traverse(el => {
            if (el.name === 'env light') {
                el.intensity = intensity;
            } else if (el.name === 'building light') {
                el.intensity = intensity * 3;
            }
        });
    }

    updateNightBuildings(alpha) {
        OrbitDayAndNight.buildingMaterials.forEach(data => {
           data.material[data.key] = 1 - alpha;
        });
    }

    updateBuildingsEmissive() {
        if (!OrbitDayAndNight.usePresets) return;
        // if (this.currentPreset.buildingEmissiveIntensity === 0) return;
        OrbitDayAndNight.preloaded.materials.forEach(material => {
            if (this.reset) {
                if (material.emissiveMap) {
                    material.emissive.setStyle(material.userData.defaultSettings
                        ? material.userData.defaultSettings.emissive : '#000000',LinearSRGBColorSpace);
                    material.emissiveIntensity = material.userData.defaultSettings
                        ? material.userData.defaultSettings.emissiveIntensity
                        : this.currentPreset.buildingEmissiveIntensity;
                } else {
                    material.lightMapIntensity = material.userData.defaultSettings
                        ? material.userData.defaultSettings.lightMapIntensity
                        : this.currentPreset.buildingEmissiveIntensity;
                }
            } else {
                if (material.emissiveMap) {
                    material.emissive.setStyle('#ffffff',LinearSRGBColorSpace);
                    material.emissiveIntensity = this.currentPreset.buildingEmissiveIntensity;
                } else {
                    material.lightMapIntensity = this.currentPreset.buildingEmissiveIntensity;
                }
            }
        });
    }

    buildSunPath() {
        const axis = new Vector3(0,-1,0).normalize();
        const polarAxis = new Vector3(1,0,0);
        const azimuth = new Euler(0,config.threejs.compass.correction - Math.PI,0,'XYZ');

        for (let i = -0.02; i < 3.16; i +=0.01){

            const v = new Vector3(-1,0,0).applyAxisAngle(axis,i);
            v.applyAxisAngle(polarAxis,MathUtils.degToRad(90 - this.center.lat));
            v.setLength(700);
            v.applyEuler(azimuth);

            this.points.push(v);
        }

        this.pointsBeforeSunrise = [];
        for (let i = -1.55; i < -0.02; i +=0.01){

            const v = new Vector3(-1,0,0).applyAxisAngle(axis,i);
            v.applyAxisAngle(polarAxis,MathUtils.degToRad(90 - this.center.lat));
            v.setLength(700);
            v.applyEuler(azimuth);

            this.pointsBeforeSunrise.push(v);
        }

        this.pointsAfterSunset = [];
        for (let i = 3.16; i < 4.7; i +=0.01){
            
            const v = new Vector3(-1,0,0).applyAxisAngle(axis,i);
            v.applyAxisAngle(polarAxis,MathUtils.degToRad(90 - this.center.lat));
            v.setLength(700);
            v.applyEuler(azimuth);

            this.pointsAfterSunset.push(v);
        }

        this.curve = new CatmullRomCurve3(this.points);
        this.curveBeforeSunrise = new CatmullRomCurve3(this.pointsBeforeSunrise);
        this.curveAfterSunset = new CatmullRomCurve3(this.pointsAfterSunset);
    }

    static preload(loader,parentGroup) {
        if (BmbyMode.active)
            return;

        // if (!OrbitDayAndNight.active) return;
        if (!OrbitDayAndNight.preloaded) {
            OrbitDayAndNight.preloaded = {};
            OrbitDayAndNight.preloaded.materials = [];
        };

       const textureLoader = loader.textureLoader;
       const url = `${config.cdnRepository}/${config.projectFolder}/${resourcesFolder}/textures/building/`;

       // For sun
       // OrbitDayAndNight.preloaded.lensFlare = textureLoader.load('./assets/textures/lensflare/lensflare3.png',loadedTexture => threeJsVariables.renderer.initTexture(loadedTexture));

       const sunGroup = OrbitDayAndNight.addSun();
       parentGroup.add(sunGroup);

       // Load and apply additional textures
       const texturesBundle = {};
       const textures = config.features.dayNight?.buildingAdditionalTextures || [];

       textures.forEach(obj => {

           const targetModel = threeJsVariables.scene.getObjectByName(obj.model);

           if (targetModel) {

               Object.entries(obj.material).forEach(([key,value]) => {

                   if (!texturesBundle[url + value]) {
                       texturesBundle[url + value] = textureLoader.load(url + value,loadedTexture => threeJsVariables.renderer.initTexture(loadedTexture));
                   }

                   if (targetModel.isGroup) {
                       const targetMaterial = targetModel.children[0].material;

                       if (Array.isArray(targetMaterial)) {
                           targetMaterial.forEach(subMaterial => {
                               if (key === 'emissiveMap') {
                                   subMaterial.emissive.setStyle('#ffffff',LinearSRGBColorSpace);
                                   subMaterial.emissiveIntensity = 0;
                               }

                               subMaterial[key] = texturesBundle[url + value];
                               OrbitDayAndNight.preloaded.materials.push(subMaterial);
                           });
                       } else {
                           if (key === 'emissiveMap') {
                               targetModel.children[0].material.emissive.setStyle('#ffffff',LinearSRGBColorSpace);
                               targetModel.children[0].material.emissiveIntensity = 0;
                           }

                           targetModel.children[0].material[key] = texturesBundle[url + value];
                           OrbitDayAndNight.preloaded.materials.push(targetModel.children[0].material);
                       }

                   } else {

                       if (key === 'emissiveMap') {
                           targetModel.material.emissive.setStyle('#ffffff',LinearSRGBColorSpace);
                           targetModel.material.emissiveIntensity = 0;
                       }

                       targetModel.material[key] = texturesBundle[url + value];
                       OrbitDayAndNight.preloaded.materials.push(targetModel.material);
                   }

               });
           }

       });
    }

    static getScope() {
        return new Promise(resolve => {
            const interval = setInterval(() => {
                if (OrbitDayAndNight.scope) {
                    clearInterval(interval);
                    resolve(OrbitDayAndNight.scope);
                }
            },100);
        });
    }

    static presetsToggle(state) {
        if (!OrbitDayAndNight.osmSelected) return;

        switch (state) {
            case 'reset':
                OrbitDayAndNight.getScope().then(scope => scope.resetToDefault());
                break;
            case 'restore':
                OrbitDayAndNight.getScope().then(scope => scope.restorePreviouslyPreset());
                break;
        }
    }

    static async turnOff() {
        // if (config.features.privateHouseMode || isCylinderDisplayModeEqual('show all')) return new Promise(resolve => {
        //     // OrbitDayAndNight.presetsToggle('reset');
        //     resolve()
        // });
        return new Promise((resolve) => {
            if (!OrbitDayAndNight.isActive || BmbyMode.active) {
                resolve();
            }
            if (PublicLayers.activeLayer?.animateCamera) {
                PublicLayers.hide();
                PublicLayers.mouseOutHandler();
            }
            if (Environment.getActivePublicLayer()?.animateCamera) {
                Environment.hide();
                Environment.mouseOutHandler();
            }
            const switcher = document.querySelector('.environment-toggle-switcher');
            const toggler = switcher.querySelector('.switch__checkbox');

            DayNightButton.scope?.resetToDefault();
            if (OrbitDayAndNight.isActiveAndSelected) {
                toggler.checked = false;
                toggler.dispatchEvent(new Event('change'));
            }

            setTimeout(() => { resolve(true) }, 100);
        });
    }

    static turnOn() {
        if (OrbitDayAndNight.isActive && !OrbitDayAndNight.isActiveAndSelected) {
            const switcher = document.querySelector('.environment-toggle-switcher');
            const toggler = switcher.querySelector('.switch__checkbox');
            toggler.checked = true;
            toggler.dispatchEvent(new Event('change'));
        }
    }
}


function getCastShadow () {
    if (threeJsVariables.bundleConfig && threeJsVariables.bundleConfig.version) {
        return threeJsVariables.bundleConfig.lights.directionalLight.castShadow;
    } else {
        return config.threejs.lights.directionalLight.castShadow;
    }
}
